import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API } from "@/App";
import { PAID_PLAN } from "@/utils/quota";

/**
 * PaidPlanModal — single Semester Plan card, Paytm checkout wired.
 * Props:
 *   open    {bool}    controls visibility
 *   onClose {fn}      called on close
 *   reason  {string}  optional banner message shown above the plan card
 *                     e.g. "Your free tier monthly limit has been reached..."
 */
export default function PaidPlanModal({ open, onClose, reason }) {
  const [loading, setLoading] = useState(false);

  const handleChoosePlan = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/create-checkout`, {
        package_id: PAID_PLAN.pkg_id,
      });
      const { order_id, txn_token, amount, mid } = data;
      sessionStorage.setItem("paytm_order_id", order_id);

      if (!window.Paytm) {
        const scriptSrc = `https://securestage.paytmpayments.com/merchantpgpui/checkoutjs/merchants/${mid}.js`;
        await new Promise((res, rej) => {
          const s = document.createElement("script");
          s.src = scriptSrc;
          s.crossOrigin = "anonymous";
          s.onload = res;
          s.onerror = () => rej(new Error("Failed to load payment SDK"));
          document.head.appendChild(s);
        });
      }

      if (window.Paytm?.CheckoutJS) {
        onClose();
        await window.Paytm.CheckoutJS.init({
          merchant: { mid, redirect: false },
          data: { orderId: order_id, token: txn_token, tokenType: "TXN_TOKEN", amount },
          handler: {
            notifyMerchant: (event) => {
              if (event === "SESSION_EXPIRED")
                toast.error("Payment session expired. Please try again.");
            },
          },
        });
        window.Paytm.CheckoutJS.invoke();
      } else {
        toast.error("Payment window could not open. Please refresh and try again.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#2E3A59]">Unlock full practice</DialogTitle>
        </DialogHeader>

        {/* ── Reason banner — shown when quota is exhausted ── */}
        {reason && (
          <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 font-medium leading-snug">{reason}</p>
          </div>
        )}

        {/* Single plan card */}
        <div className="relative border-2 border-[#C9A227] rounded-2xl p-6 bg-white mt-2">
          <span className="absolute -top-3 left-5 text-xs font-bold px-3 py-1 bg-[#C9A227] text-[#1A2236] rounded-full">
            {PAID_PLAN.badge}
          </span>

          <div className="flex items-end gap-1 mb-1">
            <span className="text-4xl font-extrabold text-[#2E3A59]">₹{PAID_PLAN.price_inr}</span>
          </div>
          <p className="text-sm text-slate-500 mb-5">{PAID_PLAN.headline}</p>

          <ul className="space-y-2.5 mb-6">
            {PAID_PLAN.includes.map((item) => (
              <li key={item} className="flex items-center gap-2.5">
                <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <span className="text-sm text-slate-700">{item}</span>
              </li>
            ))}
          </ul>

          {PAID_PLAN.note && (
            <p className="text-xs text-slate-400 mb-4">{PAID_PLAN.note}</p>
          )}

          <button
            onClick={handleChoosePlan}
            disabled={loading}
            className={`w-full py-3 rounded-xl text-sm font-bold transition-all
              bg-[#C9A227] hover:bg-[#b8911f] text-[#1A2236]
              ${loading ? "opacity-70 cursor-wait" : ""}`}
          >
            {loading ? "Opening payment…" : "Get full access →"}
          </button>
        </div>

        <p className="text-xs text-slate-400 text-center mt-1">
          Secure payment via Paytm
        </p>
      </DialogContent>
    </Dialog>
  );
}